import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Empty, ui } from '../ui';
import { formatDate, formatElapsed, formatMoney } from '@goodbuild/rules/format';
import { formatPlaytime } from '@goodbuild/rules/refund-policy';
import type { RefundQueueRow } from '@api/views';
import { ApiError, useApi, useApiMutation } from '../api';
import { Field, Notice, Screen, Table } from './shared';

/**
 * Refund requests, waiting on a person.
 *
 * **Approving is what moves the money.** The buyer's button files a request and
 * stops; `POST /admin/decisions/refunds` is what calls the provider, writes the
 * reversal and takes the game out of their library. So this screen is the only
 * place a refund happens, and every row on it is money that has not moved yet.
 *
 * **Every row is already eligible under the policy**, because a request cannot
 * be filed against an order `decideSelfServeRefund` refuses. What is on the row
 * beyond that is the part a rule cannot judge: how long they actually played,
 * how soon after buying they asked, and how many refunds this account has
 * already had. Five accounts asking for the same title in an hour is the
 * pattern the queue exists to make visible, and no per-order rule can see it.
 *
 * The decision is re-checked server-side when it is taken, so a row that has
 * gone stale — they played another two hours while it sat here — refuses on
 * approval rather than being granted from a figure that was true this morning.
 */
export function RefundQueueRoute(): React.ReactElement {
  const key = ['admin', 'queues', 'refunds'] as const;
  const query = useApi<{ rows: RefundQueueRow[] }>(key, '/admin/decisions/queues/refunds');
  const call = useApiMutation();
  const cache = useQueryClient();

  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const rows = query.data?.rows ?? [];

  async function decide(row: RefundQueueRow, decision: 'approve' | 'reject'): Promise<void> {
    setBusy(row.id);
    setMessage(null);
    setFailure(null);

    try {
      const result = await call<{ ok: string }>('/admin/decisions/refunds', {
        method: 'POST',
        body: JSON.stringify({
          refundRequestId: row.id,
          decision,
          note: notes[row.id]?.trim() || undefined,
        }),
      });
      setMessage(result.ok);
      await Promise.all([
        cache.invalidateQueries({ queryKey: key }),
        cache.invalidateQueries({ queryKey: ['admin', 'overview'] }),
      ]);
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Screen title="Refunds" aside={`${rows.length} waiting`} query={query}>
      {message ? (
        <p className="gb-body" role="status">
          {message}
        </p>
      ) : null}

      {failure ? <Notice alert>{failure}</Notice> : null}

      {rows.length === 0 ? (
        <Empty>Nobody is waiting on a refund.</Empty>
      ) : (
        <div className={ui.stack ?? ''}>
          {rows.map((row) => (
            <div key={row.id} className={ui.panel ?? ''}>
              <p className="gb-body">
                <strong>{row.titles.map((title) => title.productName).join(', ')}</strong>
                {' — '}
                <span className={ui.numeric ?? ''}>
                  {formatMoney(row.currency, row.amountMinor)}
                </span>
              </p>

              <Table head={['', '']}>
                <tr>
                  <td>Who</td>
                  <td>
                    {row.buyerName ?? 'No name'}
                    <br />
                    <span className={ui.statLabel ?? ''}>{row.buyerEmail ?? 'No address'}</span>
                  </td>
                </tr>
                <tr>
                  <td>Asked</td>
                  <td>
                    {formatElapsed(new Date(row.requestedAt))} — {formatDate(row.requestedAt)}
                  </td>
                </tr>
                <tr>
                  <td>Bought</td>
                  <td>
                    {formatElapsed(new Date(row.placedAt))} — {formatDate(row.placedAt)}
                  </td>
                </tr>
                <tr>
                  <td>Played</td>
                  <td>
                    {row.titles
                      .map(
                        (title) => `${title.productName}: ${formatPlaytime(title.playtimeMinutes)}`,
                      )
                      .join(' · ')}
                  </td>
                </tr>
                <tr>
                  <td>Refunds before</td>
                  <td className={ui.numeric ?? ''}>
                    {row.priorRefunds}
                    {row.priorRefunds > 0 ? (
                      <>
                        <br />
                        <span className={ui.statLabel ?? ''}>
                          Granted to this account already. Worth a look.
                        </span>
                      </>
                    ) : null}
                  </td>
                </tr>
                <tr>
                  <td>They said</td>
                  <td>{row.reason ?? 'Nothing.'}</td>
                </tr>
                <tr>
                  <td>Still eligible</td>
                  <td>
                    {row.decision.eligible
                      ? 'Yes.'
                      : row.decision.refusals.map((refusal) => refusal.message).join(' ')}
                  </td>
                </tr>
              </Table>

              <Field label="A note, shown to them if you reject (optional)">
                <input
                  className={ui.input ?? ''}
                  value={notes[row.id] ?? ''}
                  onChange={(event) =>
                    setNotes((current) => ({ ...current, [row.id]: event.target.value }))
                  }
                  maxLength={2000}
                />
              </Field>

              <div className={ui.actions ?? ''}>
                <button
                  type="button"
                  className={ui.button ?? ''}
                  disabled={busy === row.id || !row.decision.eligible}
                  onClick={() => void decide(row, 'approve')}
                >
                  {busy === row.id ? 'Working…' : 'Approve and refund'}
                </button>
                <button
                  type="button"
                  className={ui.buttonSecondary ?? ''}
                  disabled={busy === row.id}
                  onClick={() => void decide(row, 'reject')}
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}
